import { useEffect, useState } from 'react'
import { authFetch } from '@/api/client'
import { apiUrl } from '@/lib/apiUrl'
import { createMediaSession, FIRST_START_WAIT_MS } from '@/lib/mediaSession'

const END_TIMEOUT_MS = 3000

async function startOnServer(): Promise<number | null> {
  // ``include``: the API is another origin in production, and only a
  // credentialed fetch lets the browser keep the cookie it sets.
  const res = await authFetch('/api/v1/media/session', { method: 'POST', credentials: 'include' })
  if (!res.ok) return null
  const body = (await res.json()) as { data?: { expires_at?: number } }
  return body.data?.expires_at ?? null
}

async function endOnServer(): Promise<void> {
  // Awaited before Clerk's sign-out (see signOutWithMedia), so no keepalive:
  // some browsers refuse keepalive on a request that needs a CORS preflight,
  // and the failure would be silent. The timeout keeps "Sair" from hanging
  // on a slow or absent API; the cookie then just expires.
  await fetch(apiUrl('/api/v1/media/session'), {
    method: 'DELETE',
    credentials: 'include',
    signal: AbortSignal.timeout(END_TIMEOUT_MS),
  })
}

/** One per app: the sign-out buttons and the hook below share it. */
export const mediaSession = createMediaSession({ start: startOnServer, end: endOnServer })

/**
 * Sign out of Clerk after dropping the media cookie.
 *
 * Use this instead of Clerk's ``signOut`` alone: it reloads the window right
 * away, and a DELETE fired from an effect afterwards wouldn't make it, which
 * left the owner's cookie valid for up to 12 h after "Sair".
 */
export async function signOutWithMedia(signOut: () => Promise<unknown>): Promise<void> {
  await mediaSession.signOut()
  await signOut()
}

/**
 * Keeps the media cookie in step with Clerk's session (see ``lib/mediaSession``).
 *
 * Returns whether media may render: right away when this user's cookie is
 * likely still valid (a reload within its 12 h), else once the first start
 * settles or ``FIRST_START_WAIT_MS`` passes. That holds on every sign-in, not
 * only on a reload: signing in from /login happens inside the SPA, and the
 * first page's covers would otherwise be asked for before the cookie exists.
 */
export function useMediaSession(
  isLoaded: boolean,
  isSignedIn: boolean | undefined,
  userId: string | null | undefined,
): boolean {
  const [ready, setReady] = useState(() => mediaSession.hasFreshCookie(userId))

  useEffect(() => {
    if (!isLoaded) return
    if (!isSignedIn || !userId) {
      setReady(true)
      // A sign-out that didn't go through signOutWithMedia, or a Clerk
      // session that ended with the app closed, leaves one behind.
      if (mediaSession.hasRemembered()) void mediaSession.signOut()
      return
    }
    let settled = mediaSession.hasFreshCookie(userId)
    setReady(settled)
    const finish = () => {
      if (settled) return
      settled = true
      setReady(true)
    }
    const wait = setTimeout(finish, FIRST_START_WAIT_MS)
    void mediaSession.signIn(userId).finally(finish)
    // A laptop lid closed past the renewal timer: renew on the way back.
    const onVisible = () => {
      if (document.visibilityState === 'visible') mediaSession.wake()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearTimeout(wait)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [isLoaded, isSignedIn, userId])

  return ready
}
